import { Badge, Button, Dialog, Pane, Spinner, Table, TextInputField } from 'evergreen-ui';
import { useEffect, useMemo, useState } from 'react';
import Request from '../../../util/Api';
import Config from '../Config';

const PAGE_SIZE = 100;

const CardTraderExpansionPickerModal = (props) => {
    const { isShown, onClose, onPick, cardGameId } = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expansions, setExpansions] = useState([]);
    const [search, setSearch] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    useEffect(() => {
        if (!isShown) {
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const query = {};
                if (cardGameId) {
                    query.cardGameId = cardGameId;
                }
                const res = await Request.get(Config.Endpoints.cardTraderExpansions, {}, query);
                if (cancelled) return;
                setExpansions(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                if (cancelled) return;
                setError('Eroare la incarcarea expansiunilor de pe CardTrader.');
                setExpansions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        setSearch('');
        setVisibleCount(PAGE_SIZE);

        return () => { cancelled = true; };
    }, [isShown, cardGameId]);

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) return expansions;
        return expansions.filter((e) => {
            const name = (e.name || '').toLowerCase();
            const code = (e.code || '').toLowerCase();
            return name.includes(needle) || code.includes(needle);
        });
    }, [expansions, search]);

    // Reset pagination whenever the search filter changes.
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [search]);

    const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
    const hasMore = filtered.length > visible.length;

    return (
        <Dialog
            width={1100}
            isShown={isShown}
            title="Populeaza din CardTrader"
            hasFooter={false}
            onCloseComplete={onClose}
            onClose={onClose}
        >
            <Pane>
                <TextInputField
                    label="Cautare"
                    placeholder="Cautare dupa nume sau cod"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Pane>

            {loading && (
                <Pane display="flex" alignItems="center" justifyContent="center" padding={24}>
                    <Spinner />
                </Pane>
            )}

            {error && (
                <Pane padding={16} color="danger">{error}</Pane>
            )}

            {!loading && !error && (
                <Table>
                    <Table.Head>
                        <Table.TextHeaderCell width={100}>CT ID</Table.TextHeaderCell>
                        <Table.TextHeaderCell>Nume</Table.TextHeaderCell>
                        <Table.TextHeaderCell width={140}>Code</Table.TextHeaderCell>
                        <Table.TextHeaderCell width={220}>Game</Table.TextHeaderCell>
                        <Table.TextHeaderCell width={180}>Status</Table.TextHeaderCell>
                        <Table.TextHeaderCell width={140}>Actiuni</Table.TextHeaderCell>
                    </Table.Head>
                    <Table.Body height={420}>
                        {visible.map((expansion) => (
                            <Table.Row key={expansion.id} isSelectable>
                                <Table.TextCell>{expansion.id}</Table.TextCell>
                                <Table.TextCell>{expansion.name}</Table.TextCell>
                                <Table.TextCell>{expansion.code}</Table.TextCell>
                                <Table.TextCell>
                                    {expansion.gameName
                                        ? `${expansion.gameName} (#${expansion.cardGameId} / ct ${expansion.game_id})`
                                        : `ct ${expansion.game_id}`}
                                </Table.TextCell>
                                <Table.TextCell>
                                    {expansion.existing
                                        ? <Badge color="orange">Deja existenta</Badge>
                                        : <Badge color="green">Noua</Badge>}
                                </Table.TextCell>
                                <Table.TextCell>
                                    <Button
                                        type="button"
                                        appearance={expansion.existing ? 'default' : 'primary'}
                                        onClick={() => {
                                            onPick(expansion);
                                            onClose();
                                        }}
                                    >
                                        Selecteaza
                                    </Button>
                                </Table.TextCell>
                            </Table.Row>
                        ))}
                        {filtered.length === 0 && (
                            <Pane padding={16}>Niciun rezultat.</Pane>
                        )}
                    </Table.Body>
                </Table>
            )}

            {!loading && !error && (hasMore || filtered.length > 0) && (
                <Pane display="flex" alignItems="center" justifyContent="space-between" paddingY={12}>
                    <Pane color="muted">
                        Afiseaza {visible.length} din {filtered.length}
                        {filtered.length !== expansions.length ? ` (din ${expansions.length} totale)` : ''}
                    </Pane>
                    {hasMore && (
                        <Button
                            type="button"
                            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                        >
                            Vezi mai multe
                        </Button>
                    )}
                </Pane>
            )}
        </Dialog>
    );
};

export default CardTraderExpansionPickerModal;
